import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as R from 'Router/routes';
import { Button, Link, Modal, Typography } from 'components';
import ChevronIcon from 'assets/icons/chevron-left.svg?react';
import EmailIcon from 'assets/icons/ico-mail.svg?react';
import MobilePhoneIcon from 'assets/icons/ico-mobile.svg?react';
import IDCardIcon from 'assets/icons/ico-id.svg?react';
import InternetBankingIcon from 'assets/icons/bank_v2.svg?react';
import WalletIcon from 'assets/icons/wallet.svg?react';
import BankIdIcon from 'assets/icons/bank-id_v2.svg?react';
import styles from './Intro.module.scss';
import BankIDModal from './BankIDModal';
import InternetBankingModal from './InternetBankingModal';
import { twMerge } from 'tailwind-merge';
import { useSearchParams } from 'react-router';
import {
  EMBEDDED_IN_WEBVIEW_PARAM,
  MOBILE_SOURCE_PARAM,
} from '../../../constants';

type OpenedModal = 'bank_id' | 'internet_banking' | null;

function IntroPage() {
  const { t } = useTranslation();
  const [openedModal, setOpenedModal] = useState<OpenedModal>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get(EMBEDDED_IN_WEBVIEW_PARAM) === 'true') {
      sessionStorage.setItem(EMBEDDED_IN_WEBVIEW_PARAM, 'true');
    }
    const source = searchParams.get(MOBILE_SOURCE_PARAM);
    if (source) {
      sessionStorage.setItem(MOBILE_SOURCE_PARAM, source);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('intro:html_title')}</title>
      </Helmet>

      {/* <VideoBannerPortal
        classNameContainer={styles['video-banner-wrapper']}
        content={{
          title: 'intro:video_banner_title',
          description: 'intro:video_banner_description',
          link: 'intro:video_banner_link',
        }}
      /> */}

      <Typography
        className={styles.title}
        kind="h1"
        size="xl"
        fontWeight={700}
        dataTest="intro-title"
      >
        {t('intro:title')}
      </Typography>

      <section data-test="intro-section-login">
        <Typography
          fontWeight={700}
          className={styles.customer}
          dataTest="intro-customer-title"
        >
          {t('intro:customer_title')}
        </Typography>
        <Typography
          className={styles.customer}
          fontWeight={400}
          size="s"
          dataTest="intro-customer-message"
        >
          {t('intro:customer_message')}
          {' – '}
          <Link
            external
            fontWeight={400}
            to={import.meta.env.VITE_DASHBOARD_URL}
            dataTest="intro-login-link"
          >
            {t('intro:login')}
          </Link>
          {'.'}
        </Typography>
      </section>

      <section data-test="intro-section-notes">
        <Typography
          className={styles.info}
          kind="h3"
          size="m"
          fontWeight={700}
          dataTest="intro-info"
        >
          {t('intro:info')}
        </Typography>

        <div className={styles.card}>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <IDCardIcon
                className={styles.icon}
                data-test="intro-idcard-icon"
              />
            </div>
            <Typography
              fontWeight={500}
              className={styles['info-container-font']}
              dataTest="intro-id"
            >
              {t('intro:id')}
            </Typography>
          </div>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <MobilePhoneIcon
                className={styles.icon}
                data-test="intro-phone-icon"
              />
            </div>
            <Typography
              fontWeight={500}
              className={styles['info-container-font']}
              dataTest="intro-phone-text"
            >
              {t('intro:mobile')}
            </Typography>
          </div>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <EmailIcon className={styles.icon} data-test="intro-email-icon" />
            </div>
            <Typography
              fontWeight={500}
              className={styles['info-container-font']}
              dataTest="intro-email-text"
            >
              {t('intro:email')}
            </Typography>
          </div>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <WalletIcon className={styles.icon} />
            </div>
            <Typography
              fontWeight={500}
              className={styles['info-container-font']}
            >
              {t('intro:wallet')}
            </Typography>
          </div>
        </div>
      </section>

      <section data-test="intro-section-bank-id">
        <Typography
          className={styles.info}
          kind="h3"
          size="m"
          fontWeight={700}
          dataTest="intro-info-section"
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t('intro:info_optional'),
            }}
            data-test="intro-info-optional"
          />
          <BankIdIcon />
        </Typography>

        <div className={styles.bankIdWrapper} data-test="bank-id-card-wrapper">
          <div
            className={styles.moneyInFewMins}
            data-test="bank-id-card-top-bar"
          >
            {t('intro:moneyInFewMins')}
          </div>
          <div
            className={styles.bankIdCard}
            data-test="bank-id-card-icon-wrapper"
          >
            <div
              className={twMerge(styles['icon-container'], styles['bank-icon'])}
              data-test="bank-id-card-icon-container"
            >
              <InternetBankingIcon data-test="intro-banking-icon" />
            </div>
            <div
              className={styles.column}
              data-test="bank-id-card-text-container"
            >
              <div className={styles['bankId-info-container']}>
                <Typography
                  fontWeight={500}
                  className={twMerge(styles['info-bank-container-font'])}
                  dataTest="bank-id-card-text"
                >
                  {t('intro:bank_id')}
                </Typography>
                <Link
                  dataTest="bank-id-card-link"
                  className={styles['more-information']}
                  onClick={() => setOpenedModal('bank_id')}
                  size="xs"
                >
                  {t('intro:more_information')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className={styles.cntBtnWrapper}
        data-test="intro-section-button"
      >
        <Button
          dataTest="continue"
          className={styles.continue}
          to={R.PERSONAL_DETAILS}
          type="submit"
        >
          {t('common:continue')}
        </Button>

        <div>
          <Link
            dataTest="back"
            fontWeight={400}
            to={`${import.meta.env.VITE_HOMEPAGE_URL}`}
            external
          >
            <ChevronIcon
              className={styles.chevron}
              data-test="intro-chevron-icon"
            />
            {t('common:back')}
          </Link>
        </div>
      </section>

      {openedModal === 'bank_id' && (
        <Modal className={styles.modal} onClose={() => setOpenedModal(null)}>
          <BankIDModal />
        </Modal>
      )}

      {openedModal === 'internet_banking' && (
        <Modal className={styles.modal} onClose={() => setOpenedModal(null)}>
          <InternetBankingModal />
        </Modal>
      )}
    </>
  );
}

export default IntroPage;
